import React, { useEffect, useMemo, useState } from "react";
import { fetchClientsForPicker } from "../services/CrmClientPickerRepository";

const getRoleLabel = (role) => {
  const labels = {
    CLIENT: "Cliente",
    PARTNER: "Parceiro",
    OPPONENT: "Adverso",
    OTHER: "Outro",
  };
  const safeRole = String(role || "OTHER").toUpperCase();
  return labels[safeRole] || safeRole;
};

const getClientEntityIconClass = (clientType) => {
  const safeType = String(clientType || "")
    .trim()
    .toLowerCase();
  if (safeType.includes("animal")) return "fa-paw";
  if (safeType.includes("jurídica") || safeType.includes("juridica"))
    return "fa-building";
  return "fa-user";
};

export const CrmClientPickerModal = ({
  isOpen,
  onClose,
  onSelect,
  db,
  loadClients,
  appId,
  title,
  requiredRole,
  requiredType,
  initialLimit = 200,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const norm = (v) => {
    return String(v || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  useEffect(() => {
    if (!isOpen) return;

    let alive = true;
    const run = async () => {
      setLoading(true);
      setLoadError("");
      try {
        let rows = [];
        if (typeof loadClients === "function") {
          rows = await loadClients();
        } else if (appId) {
          rows = await fetchClientsForPicker(appId, initialLimit);
        }
        if (!alive) return;
        setClients(rows);
      } catch (e) {
        console.error(e);
        setLoadError(
          "Erro ao carregar base do CRM: " +
            (e?.message || "erro desconhecido"),
        );
        setClients([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [isOpen, db, loadClients, appId, initialLimit, requiredType]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm("");
    setLoadError("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (typeof onClose === "function") onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, onClose]);

  const filteredClients = useMemo(() => {
    const term = String(searchTerm || "")
      .trim()
      .toLowerCase();

    const base = Array.isArray(clients) ? clients : [];

    // Filtra soft deletes (clientes apagados)
    const active = base.filter((c) => c.isDeleted !== true);

    const byType = requiredType
      ? active.filter((c) => {
          const t = norm(c?.type);
          const r = norm(requiredType);

          if (!t) return false;
          if (t === r) return true;

          if (
            r === "pessoa fisica" ||
            r === "pessoa física" ||
            r === "physical"
          ) {
            return (
              t === "pessoa fisica" || t === "pessoa física" || t === "physical"
            );
          }
          if (
            r === "pessoa juridica" ||
            r === "pessoa jurídica" ||
            r === "legal"
          ) {
            return (
              t === "pessoa juridica" ||
              t === "pessoa jurídica" ||
              t === "legal"
            );
          }

          return false;
        })
      : active;

    const byRole = requiredRole
      ? byType.filter(
          (c) => Array.isArray(c.roles) && c.roles.includes(requiredRole),
        )
      : byType;

    const searched = !term
      ? byRole
      : byRole.filter((c) => {
          const name =
            c?.name ||
            c?.qualification?.physical?.name ||
            c?.qualification?.legal?.companyName ||
            c?.qualification?.animal?.name ||
            c?.qualification?.minor?.name ||
            "";
          const cpf = c?.cpf || c?.qualification?.physical?.cpf || "";
          const cnpj = c?.cnpj || c?.qualification?.legal?.cnpj || "";
          const email = c?.contact?.email || c?.contact?.emailPrimary || "";
          const qualificationText = c?.qualificationText || "";

          const hay =
            `${name} ${cpf} ${cnpj} ${email} ${qualificationText}`.toLowerCase();
          const normalizedHay = hay
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
          const normalizedTerm = term
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
          const digitHay = `${cpf} ${cnpj}`.replace(/\D/g, "");
          const digitTerm = term.replace(/\D/g, "");
          return (
            normalizedHay.includes(normalizedTerm) ||
            (digitTerm ? digitHay.includes(digitTerm) : false)
          );
        });

    // Ordenação alfabética A-Z
    const resolveSortName = (c) => {
      const t = String(c?.type || "").toUpperCase();
      if (t === "PESSOA FÍSICA" || t === "PHYSICAL")
        return (
          c?.qualification?.physical?.socialName ||
          c?.qualification?.physical?.name ||
          ""
        );
      if (t === "PESSOA JURÍDICA" || t === "PESSOA JURIDICA" || t === "LEGAL")
        return c?.qualification?.legal?.companyName || "";
      if (
        t === "ANIMAL" ||
        t === "ANIMAL NÃO-HUMANO" ||
        t === "ANIMAL NAO-HUMANO"
      )
        return c?.qualification?.animal?.name || "";
      if (t === "MENOR" || t === "MINOR")
        return c?.qualification?.minor?.name || "";
      if (t === "ESPÓLIO" || t === "ESPOLIO" || t === "ESTATE")
        return c?.qualification?.estate?.deceasedName || "";
      if (t === "CONDOMÍNIO" || t === "CONDOMINIO" || t === "CONDOMINIUM")
        return c?.qualification?.condominium?.name || "";
      return c?.name || c?.qualificationText || "";
    };

    return [...searched].sort((a, b) => {
      const na = String(resolveSortName(a)).trim();
      const nb = String(resolveSortName(b)).trim();
      return na.localeCompare(nb, "pt-BR", { sensitivity: "base" });
    });
  }, [clients, requiredRole, requiredType, searchTerm]);

  const resolveName = (c) => {
    const t = String(c?.type || "").toUpperCase();
    if (t === "PESSOA FÍSICA" || t === "PHYSICAL") {
      return c?.qualification?.physical?.name || c?.name || "Pessoa Física";
    }
    if (t === "PESSOA JURÍDICA" || t === "PESSOA JURIDICA" || t === "LEGAL") {
      return (
        c?.qualification?.legal?.companyName || c?.name || "Pessoa Jurídica"
      );
    }
    if (
      t === "ANIMAL" ||
      t === "ANIMAL NÃO-HUMANO" ||
      t === "ANIMAL NAO-HUMANO"
    ) {
      return c?.qualification?.animal?.name || c?.name || "Animal";
    }
    if (t === "MENOR" || t === "MINOR") {
      return c?.qualification?.minor?.name || c?.name || "Menor";
    }
    if (t === "ESPÓLIO" || t === "ESPOLIO" || t === "ESTATE") {
      return c?.qualification?.estate?.deceasedName || c?.name || "Espólio";
    }
    if (t === "CONDOMÍNIO" || t === "CONDOMINIO" || t === "CONDOMINIUM") {
      return c?.qualification?.condominium?.name || c?.name || "Condomínio";
    }
    return c?.name || "Registro";
  };

  const resolveDoc = (c) => {
    return c?.cpf || c?.cnpj || c?.qualification?.legal?.cnpj || "";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden border border-gray-200">
        <div className="bg-[#20243B] p-4 text-white flex justify-between items-center">
          <div className="min-w-0">
            <h3 className="font-extrabold text-sm uppercase tracking-widest truncate">
              {title || "Base de Clientes (CRM)"}
            </h3>
            <div className="text-[10px] text-slate-200 mt-1">
              Pesquise e selecione um registro para importar.
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-white/15 bg-white/10 text-white/80 hover:bg-white/15 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/25"
            title="Fechar"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <input
              className="w-full pl-9 pr-4 py-2 rounded-lg text-xs outline-none border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30"
              placeholder="Nome, CPF ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            <i className="fas fa-search absolute left-3 top-2.5 text-gray-400 text-xs" />
          </div>
          {loadError ? (
            <div className="mt-2 text-[11px] font-bold text-red-600">
              {loadError}
            </div>
          ) : null}
        </div>

        <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="text-center p-10">
              <i className="fas fa-spinner fa-spin text-2xl text-brand-blue" />
            </div>
          ) : (
            <div className="w-full">
              <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 p-4">
                  <div className="text-[10px] font-bold text-gray-500 uppercase">
                    Nome / Contato
                  </div>
                  <div className="text-[10px] font-bold text-gray-500 uppercase">
                    Documento
                  </div>
                  <div className="text-[10px] font-bold text-gray-500 uppercase">
                    Tipo
                  </div>
                  <div className="text-[10px] font-bold text-gray-500 uppercase">
                    Perfis
                  </div>
                </div>
              </div>

              {filteredClients.length === 0 ? (
                <div className="p-10 text-center text-xs text-gray-400">
                  Nenhum registro encontrado.
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredClients.map((c) => {
                    const roles =
                      Array.isArray(c.roles) && c.roles.length > 0
                        ? c.roles
                        : [];
                    const clientKey = String(
                      c?.id ||
                        c?.clientId ||
                        resolveName(c) ||
                        resolveDoc(c) ||
                        Math.random(),
                    ).trim();
                    return (
                      <button
                        key={clientKey}
                        className="w-full text-left hover:bg-blue-50 transition-colors focus:outline-none focus:bg-blue-50"
                        type="button"
                        onClick={() => {
                          if (typeof onSelect === "function") onSelect(c);
                          if (typeof onClose === "function") onClose();
                        }}
                      >
                        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 p-4 items-start">
                          <div className="min-w-0">
                            <div className="font-bold text-gray-800 text-sm truncate">
                              {resolveName(c)}
                            </div>
                            <div className="text-[10px] text-gray-400 mt-1 truncate">
                              {c?.contact?.email ||
                                c?.contact?.emailPrimary ||
                                ""}
                            </div>
                          </div>
                          <div className="text-xs text-gray-700 font-mono break-words">
                            {resolveDoc(c)}
                          </div>
                          <div>
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                              <i
                                className={`fas ${getClientEntityIconClass(c?.type)} text-[10px] text-slate-400`}
                              />
                              <span>{c?.type || "—"}</span>
                            </span>
                          </div>
                          <div>
                            <div className="flex flex-wrap gap-x-2 gap-y-1 text-[10px] text-slate-500">
                              {(roles.length ? roles : ["OTHER"]).map(
                                (r, index) => (
                                  <span
                                    key={`${clientKey}-${String(r || "OTHER")}-${index}`}
                                    className="inline-flex items-center gap-1"
                                  >
                                    <i className="fas fa-id-badge text-[9px] text-slate-400" />
                                    <span>{getRoleLabel(r)}</span>
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
